// deno-lint-ignore-file
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import API from '../../services/api.js'
import { useQuestionnaireDetail, useQuestionnaires } from '../../hooks/useQuestionnaires.js'
import { ArrowLeft, Plus, Loader2, Check, X, Pencil, AlertTriangle } from 'lucide-react'
import { motion, useAnimation } from "framer-motion"

const MAPPING_POLL_INTERVAL_MS = 3000

function StatusBadge({ status }) {
  const styles = {
    draft: 'bg-gray-500/10 text-gray-400 border border-gray-500/20',
    active: 'bg-green-500/10 text-green-400 border border-green-500/20',
    archived: 'bg-gray-500/10 text-gray-500 border border-gray-500/20'
  }
  return (
    <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium inline-flex items-center ${styles[status]}`}>
      {status?.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
    </span>
  )
}

function MappingStatusBadge({ status }) {
  const styles = {
    ai_suggested: 'bg-white/5 text-gray-400 border border-white/10',
    accepted:     'bg-green-500/10 text-green-400 border border-green-500/20',
    rejected:     'bg-red-500/10 text-red-400 border border-red-500/20',
    edited:       'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  }
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bAi\b/g, 'AI')
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] || styles.ai_suggested}`}>
      {label}
    </span>
  )
}

function AddQuestionForm({ onAdd, onCancel }) {
  const [questionText, setQuestionText] = useState('')
  const [questionType, setQuestionType] = useState('yes_no')
  const [hint, setHint] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)

  const controls = useAnimation()

  const typeOptions = [
    { value: 'yes_no', label: 'Yes / No' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'text', label: 'Text' },
    { value: 'scale', label: 'Scale (1-5)' }
  ]

  const selected = typeOptions.find(o => o.value === questionType)

  function addOption() { setOptions(prev => [...prev, '']) }
  function removeOption(index) { setOptions(prev => prev.filter((_, i) => i !== index)) }
  function updateOption(index, value) { setOptions(prev => prev.map((o, i) => i === index ? value : o)) }

  async function triggerShake() {
    await controls.start({ x: [0, -8, 8, -6, 6, -3, 3, 0], transition: { duration: 0.4 } })
    await controls.start({ x: 0 })
  }

  async function handleSubmit() {
    setError(null)

    if (!questionText.trim()) {
      setError('Question text is required')
      await triggerShake()
      return
    }

    if (questionType === 'multiple_choice') {
      const validOptions = options.filter(o => o.trim())
      if (validOptions.length < 2) {
        setError('At least 2 options are required for multiple choice')
        await triggerShake()
        return
      }
    }

    setSubmitting(true)
    try {
      await onAdd({
        question_text: questionText.trim(),
        question_type: questionType,
        options: questionType === 'multiple_choice' ? options.filter(o => o.trim()) : null,
        hint: hint.trim() || null
      })
    } catch (err) {
      setError(err.message)
      await triggerShake()
    }
    setSubmitting(false)
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeInOut' }}>
      <motion.div
        animate={controls}
        className={`bg-white/5 backdrop-blur-xl rounded-xl p-5 border transition-colors duration-300 ${error ? 'border-red-500/40 shadow-[0_0_20px_rgba(239,68,68,0.15)]' : 'border-white/10'}`}
      >
        <h3 className="text-sm font-semibold text-white mb-4">Add Question</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Question Text <span className="text-red-400">*</span>
            </label>
            <textarea
              value={questionText}
              onChange={(e) => { setQuestionText(e.target.value); if (error) setError(null) }}
              placeholder="Enter your question…"
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
              rows={2}
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-400 mb-1">Question Type</label>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200 flex justify-between items-center"
            >
              <span>{selected?.label}</span>
              <span className="text-gray-500">▾</span>
            </button>

            {open && (
              <div className="absolute left-0 top-full mt-2 w-full z-50 bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden shadow-lg">
                {typeOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setQuestionType(opt.value); setOpen(false) }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {questionType === 'multiple_choice' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">
                Options <span className="text-red-400">*</span>
              </label>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                    />
                    {options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/5 hover:border-red-500/20 border border-white/10 transition-all duration-200"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addOption} className="text-xs text-blue-400 hover:text-blue-300 transition-colors mt-1">
                  + Add option
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Hint <span className="text-gray-600">(Optional)</span>
            </label>
            <input
              type="text"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="Optional guidance for the person answering"
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
            />
          </div>
        </div>

        {error && (
          <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-red-400 mt-4">
            {error}
          </motion.p>
        )}

        <div className="flex justify-end items-center gap-3 mt-6">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-gray-500/50 transition-all duration-200">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
            {submitting ? 'Adding...' : 'Add Question'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function QuestionCard({ question, index, onDelete, canEdit }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    setDeleting(true)
    try { await onDelete(question.id) } catch (err) { console.error('Failed to delete question:', err) }
    setDeleting(false)
    setConfirmDelete(false)
  }

  const typeLabels = {
    yes_no: 'Yes / No',
    multiple_choice: 'Multiple Choice',
    text: 'Text',
    scale: 'Scale (1-5)'
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-5 hover:bg-white/[0.07] transition-all duration-200">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="text-[10px] font-mono bg-white/5 border border-white/10 text-gray-500 px-2 py-0.5 rounded-md shrink-0">
            Q{index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium mb-2 leading-relaxed">{question.question_text}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] sm:text-xs bg-white/5 border border-white/10 text-gray-400 px-2 py-0.5 rounded-full">
                {typeLabels[question.question_type]}
              </span>
              {question.options && (
                <span className="text-[10px] sm:text-xs text-gray-500">{question.options.length} options</span>
              )}
            </div>
            {question.hint && <p className="text-xs text-gray-500 mt-2 italic">Hint: {question.hint}</p>}
            {question.options && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {question.options.map((opt, i) => (
                  <span key={i} className="text-[10px] sm:text-xs bg-white/5 border border-white/10 text-gray-400 px-2 py-0.5 rounded-full">
                    {opt}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            {!confirmDelete ? (
              <button type="button" onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-md text-gray-500 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-all duration-200">
                Delete
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Sure?</span>
                <button type="button" onClick={handleDelete} disabled={deleting} className="px-2 py-1 text-xs text-red-400 hover:text-red-300 transition-colors">
                  {deleting ? '...' : 'Yes'}
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-white transition-colors">
                  No
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}

function AnswerForm({ questions, onSubmit, submitting }) {
  const [answers, setAnswers] = useState({})

  function setAnswer(questionId, value) {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  function handleSubmit() {
    const allAnswered = questions.every(q => answers[q.id] !== undefined && answers[q.id] !== '')
    if (!allAnswered) { alert('Please answer all questions before submitting.'); return }
    onSubmit(answers)
  }

  return (
    <div className="space-y-4">
      {questions.map((question, index) => (
        <motion.div key={question.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: index * 0.04 }} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-5">
          <div className="flex items-start gap-3 mb-4">
            <span className="text-[10px] font-mono bg-white/5 border border-white/10 text-gray-500 px-2 py-0.5 rounded-md shrink-0">
              Q{index + 1}
            </span>
            <div>
              <p className="text-sm text-white font-medium leading-relaxed">{question.question_text}</p>
              {question.hint && <p className="text-xs text-gray-500 mt-1 italic">{question.hint}</p>}
            </div>
          </div>

          {question.question_type === 'yes_no' && (
            <div className="flex gap-3 ml-9">
              {['Yes', 'No'].map(option => (
                <button
                  type="button"
                  key={option}
                  onClick={() => setAnswer(question.id, option)}
                  className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${answers[question.id] === option ? 'bg-blue-600 text-white border border-blue-500' : 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {question.question_type === 'multiple_choice' && (
            <div className="space-y-2 ml-9">
              {question.options?.map(option => (
                <label key={option} className="flex items-center gap-3 cursor-pointer group/opt">
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={() => setAnswer(question.id, option)}
                    className="accent-blue-500"
                  />
                  <span className="text-sm text-gray-300 group-hover/opt:text-white transition-colors">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.question_type === 'text' && (
            <textarea
              value={answers[question.id] || ''}
              onChange={(e) => setAnswer(question.id, e.target.value)}
              placeholder="Your answer..."
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
              rows={3}
            />
          )}

          {question.question_type === 'scale' && (
            <div className="flex gap-2 ml-9">
              {[1, 2, 3, 4, 5].map(value => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setAnswer(question.id, String(value))}
                  className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 ${answers[question.id] === String(value) ? 'bg-blue-600 text-white border border-blue-500' : 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                >
                  {value}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      ))}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-3 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
      >
        {submitting ? 'Submitting...' : 'Submit Answers'}
      </button>
    </div>
  )
}

function NewQuestionnaireForm({ onCreate }) {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  async function handleCreate() {
    if (!title.trim()) {
      setError('Title is required')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const questionnaire = await onCreate(title.trim(), description.trim())
      navigate(`/grc/questionnaires/${questionnaire.id}`, { replace: true })
    } catch (err) {
      setError(err.message)
      setSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative w-full max-w-xl mx-auto px-3 md:px-4 py-6 sm:py-8 min-w-0">
        <div className="flex items-center gap-4 mb-8">
          <motion.button
            onClick={() => navigate('/grc/questionnaires')}
            title="Go back"
            whileHover={{ scale: 1.08, x: -2 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 260, damping: 16 }}
            className="flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight">New Questionnaire</h1>
            <div className="mt-2 h-[2px] w-20 sm:w-40 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); if (error) setError(null) }}
              placeholder="e.g. AI Vendor Risk Assessment"
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Description <span className="text-gray-600">(Optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this questionnaire for?"
              className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
              rows={3}
            />
          </div>

          {error && (
            <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-red-400">
              {error}
            </motion.p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => navigate('/grc/questionnaires')} className="px-4 py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-gray-500/50 transition-all duration-200">
              Cancel
            </button>
            <button type="button" onClick={handleCreate} disabled={submitting} className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
              {submitting ? 'Creating...' : 'Create Questionnaire'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ControlMappingCard({ mapping, onReview }) {
  const [editing, setEditing] = useState(false)
  const [editedReasoning, setEditedReasoning] = useState(mapping.reasoning || '')
  const [busy, setBusy] = useState(false)

  function getConfidenceColor(score) {
    if (score == null) return 'bg-gray-500'
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  async function handleAccept() {
    setBusy(true)
    try { await onReview(mapping.answer_id, mapping.control_id, 'accepted') } finally { setBusy(false) }
  }

  async function handleReject() {
    setBusy(true)
    try { await onReview(mapping.answer_id, mapping.control_id, 'rejected') } finally { setBusy(false) }
  }

  async function handleSaveEdit() {
    setBusy(true)
    try {
      await onReview(mapping.answer_id, mapping.control_id, 'edited', editedReasoning)
      setEditing(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`bg-white/5 border border-white/10 rounded-lg p-3 ${mapping.status === 'rejected' ? 'opacity-60' : ''}`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[10px] font-mono bg-white/5 border border-white/10 text-blue-400 px-2 py-0.5 rounded">
            {mapping.controls?.control_id}
          </span>
          <span className="text-xs sm:text-sm font-medium text-white break-words">{mapping.controls?.title}</span>
          <MappingStatusBadge status={mapping.status} />
        </div>

        {mapping.confidence_score != null && (
          <div className="flex items-center gap-2 w-full sm:w-auto sm:justify-end">
            <span className="text-[11px] text-gray-400 whitespace-nowrap">Confidence: {mapping.confidence_score}%</span>
            <div className="flex-1 sm:flex-none sm:w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${getConfidenceColor(mapping.confidence_score)}`} style={{ width: `${mapping.confidence_score}%` }} />
            </div>
          </div>
        )}
      </div>

      {(mapping.question_text || mapping.answer_value) && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
          {mapping.question_text && (
            <>
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Question</p>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed mb-2">{mapping.question_text}</p>
            </>
          )}
          {mapping.answer_value && (
            <>
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Answer</p>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{mapping.answer_value}</p>
            </>
          )}
        </div>
      )}

      {editing ? (
        <div className="mb-3">
          <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Edit reasoning</p>
          <textarea
            value={editedReasoning}
            onChange={e => setEditedReasoning(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs sm:text-sm text-gray-300 focus:outline-none focus:border-blue-500/50 focus:bg-white/10 resize-none transition"
            rows={3}
          />
        </div>
      ) : (
        <div className="mb-3">
          <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Reasoning</p>
          <p className={`text-xs sm:text-sm leading-relaxed ${(mapping.status === 'edited' ? mapping.edited_reasoning : mapping.reasoning) ? 'text-gray-400' : 'text-gray-600 italic'}`}>
            {(mapping.status === 'edited' ? mapping.edited_reasoning : mapping.reasoning) || 'No AI reasoning available for this mapping.'}
          </p>
        </div>
      )}

      {mapping.status === 'ai_suggested' && !editing && (
        <div className="flex gap-2">
          <motion.button type="button" onClick={handleAccept} disabled={busy} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }} className="flex-1 px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center gap-1.5">
            <Check className="w-3.5 h-3.5" />
            Accept
          </motion.button>

          <motion.button type="button" onClick={handleReject} disabled={busy} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }} className="flex-1 px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center gap-1.5">
            <X className="w-3.5 h-3.5" />
            Reject
          </motion.button>

          <motion.button type="button" onClick={() => setEditing(true)} disabled={busy} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }} className="flex-1 px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white disabled:opacity-50 transition-colors duration-200 flex items-center justify-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </motion.button>
        </div>
      )}

      {editing && (
        <div className="flex gap-2">
          <button type="button" onClick={handleSaveEdit} disabled={busy} className="px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 disabled:opacity-50 transition">
            {busy ? 'Saving...' : 'Save Edit'}
          </button>
          <button type="button" onClick={() => { setEditing(false); setEditedReasoning(mapping.reasoning || '') }} disabled={busy} className="px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 disabled:opacity-50 transition">
            Cancel
          </button>
        </div>
      )}

      {mapping.status !== 'ai_suggested' && !editing && mapping.reviewed_at && (
        <p className="text-[11px] text-gray-600 mt-2">
          {mapping.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} · {new Date(mapping.reviewed_at).toLocaleString()}
        </p>
      )}
    </div>
  )
}

export default function QuestionnaireDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  // Page-level asset scope — same pattern as every other GRC page.
  const selectedAppName = sessionStorage.getItem('selectedApp')
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [assetError, setAssetError] = useState(null)

  useEffect(() => {
    if (!selectedAppName) {
      setAssetError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => { setAssetId(data.id); setAssetError(null) })
      .catch(() => setAssetError('Could not resolve the selected asset. Please re-select it from the dashboard.'))
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  const { createQuestionnaire } = useQuestionnaires(assetId)
  const {
    questionnaire, questions, loading, error,
    addQuestion, deleteQuestion, updateStatus,
    submitAnswer, fetchResults, reviewMapping,
  } = useQuestionnaireDetail(id, assetId)

  const [activeTab, setActiveTab] = useState('builder')
  const [showAddQuestion, setShowAddQuestion] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const [results, setResults] = useState(null)
  const [mappingStatus, setMappingStatus] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => {
    async function fetchUser() {
      try {
        const { data } = await API.get('/auth/me/')
        setCurrentUser(data)
      } catch (err) {
        console.error('Failed to fetch current user:', err?.response?.data?.detail || err.message)
      }
    }
    fetchUser()
  }, [])

  useLayoutEffect(() => { window.scrollTo(0, 0) }, [id])

  // Poll results while mapping is still processing.
  useEffect(() => {
    if (mappingStatus?.phase !== 'processing' || !assetId) return

    let cancelled = false

    async function tick() {
      try {
        const data = await fetchResults()
        if (cancelled) return
        if (data.phase === 'done') {
          setResults(data.results)
          setMappingStatus({ phase: 'done', total: data.total, scored: data.scored })
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
        } else {
          setMappingStatus({ phase: 'processing', total: data.total, scored: data.scored })
        }
      } catch (err) {
        if (!cancelled) setSubmitError(err.message)
      }
    }

    tick()
    pollRef.current = setInterval(tick, MAPPING_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (pollRef.current) clearInterval(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingStatus?.phase, id, assetId])

  // Load existing results on mount, in case this questionnaire already
  // has scored answers from a previous session.
  useEffect(() => {
    if (!id || id === 'new' || !assetId) return
    let cancelled = false

    async function checkExisting() {
      try {
        const data = await fetchResults()
        if (cancelled) return
        if (data.total === 0) return
        if (data.phase === 'done') {
          setResults(data.results)
          setMappingStatus({ phase: 'done', total: data.total, scored: data.scored })
        } else {
          setMappingStatus({ phase: 'processing', total: data.total, scored: data.scored })
        }
      } catch (err) {
        if (!cancelled) setSubmitError(err.message)
      }
    }

    checkExisting()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assetId])

  async function handleAddQuestion(questionData) {
    await addQuestion(questionData)
    setShowAddQuestion(false)
  }

  async function handleStatusChange(newStatus) {
    try {
      await updateStatus(newStatus)
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  async function handleSubmitAnswers(answers) {
    setSubmitting(true)
    setSubmitError(null)
    try {
      await Promise.all(
        Object.entries(answers).map(([questionId, value]) => submitAnswer(questionId, value))
      )

      setResults(null)
      setActiveTab('results')
      setMappingStatus({ phase: 'processing', total: Object.keys(answers).length, scored: 0 })
    } catch (err) {
      setSubmitError(err.message)
    }
    setSubmitting(false)
  }

  async function handleReviewMapping(answerId, controlId, mappingStatusValue, editedReasoning = null) {
    try {
      await reviewMapping(answerId, controlId, mappingStatusValue, editedReasoning)
      const data = await fetchResults()
      if (data.results) setResults(data.results)
    } catch (err) {
      setSubmitError(err.message)
    }
  }

  const canEdit = currentUser?.role === 'super_admin' || currentUser?.role === 'client_admin'

  if (id === 'new') {
    return <NewQuestionnaireForm onCreate={createQuestionnaire} />
  }

  const isDraft = questionnaire?.status === 'draft'
  const isActive = questionnaire?.status === 'active'

  const tabs = [
    isDraft && canEdit && { key: 'builder', label: 'Builder' },
    isActive && { key: 'answer', label: 'Answer' },
    { key: 'questions', label: `Questions (${questions.length})` },
    (results || mappingStatus) && { key: 'results', label: 'Results' }
  ].filter(Boolean)

  if (resolvingAsset || loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (assetError) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-6 py-5 text-center max-w-md flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{assetError}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-6 py-5 text-center max-w-md">
          <p className="text-red-400 text-sm font-medium">Failed to load questionnaire</p>
          <p className="text-gray-500 text-xs mt-2">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">

        <div className="min-w-0 flex flex-col justify-center">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <motion.button
                onClick={() => navigate('/grc/questionnaires')}
                title="Go back"
                whileHover={{ scale: 1.08, x: -2 }}
                whileTap={{ scale: 0.92 }}
                transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                className="inline-flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-200 shrink-0"
              >
                <ArrowLeft className="w-5 h-5 mt-[-40px]" />
              </motion.button>

              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight truncate">
                  {questionnaire?.title}
                </h1>
                <div className="mt-2 h-[2px] w-20 sm:w-40 lg:w-64 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
                {questionnaire?.description && (
                  <p className="text-xs sm:text-sm text-gray-400 mt-2">{questionnaire.description}</p>
                )}
                <div className="mt-3">
                  <StatusBadge status={questionnaire?.status} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-[-30px]">
              {canEdit && (
                <>
                  {isDraft && questions.length > 0 && (
                    <button type="button" onClick={() => handleStatusChange('active')} className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20 hover:border-green-500/40 transition-all duration-200">
                      Activate
                    </button>
                  )}
                  {isActive && (
                    <button type="button" onClick={() => handleStatusChange('archived')} className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:border-gray-500/50 transition-all duration-200">
                      Archive
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex gap-1 mt-8 border-b border-white/10">
            {tabs.map(tab => (
              <button
                type="button"
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-xs sm:text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${activeTab === tab.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <motion.div key={activeTab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {activeTab === 'builder' && (
            <div className="space-y-4">
              {questions.length === 0 && !showAddQuestion && (
                <div className="text-center py-14 text-gray-500 px-4">
                  <p className="text-base sm:text-lg text-gray-300">No questions yet</p>
                  <p className="text-xs sm:text-sm mt-2">Add your first question to get started</p>
                </div>
              )}

              {questions.map((question, index) => (
                <QuestionCard key={question.id} question={question} index={index} onDelete={deleteQuestion} canEdit={isDraft && canEdit} />
              ))}

              {showAddQuestion ? (
                <AddQuestionForm onAdd={handleAddQuestion} onCancel={() => setShowAddQuestion(false)} />
              ) : (
                <button type="button" onClick={() => setShowAddQuestion(true)} className="w-full py-4 border border-white/10 border-dashed rounded-xl text-sm text-gray-500 hover:text-white hover:border-white/20 hover:bg-white/5 inline-flex items-center justify-center gap-2 transition-all duration-200">
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              )}
            </div>
          )}

          {activeTab === 'questions' && (
            <div className="space-y-3">
              {questions.length === 0 ? (
                <div className="text-center py-14 text-gray-500 px-4">
                  <p className="text-base sm:text-lg text-gray-300">No questions added yet</p>
                </div>
              ) : (
                questions.map((question, index) => (
                  <QuestionCard key={question.id} question={question} index={index} onDelete={deleteQuestion} canEdit={isDraft && canEdit} />
                ))
              )}
            </div>
          )}

          {activeTab === 'answer' && (
            <div>
              {submitError && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
                  {submitError}
                </motion.div>
              )}
              {questions.length === 0 ? (
                <div className="text-center py-14 text-gray-500 px-4">
                  <p className="text-base sm:text-lg text-gray-300">No questions to answer</p>
                </div>
              ) : (
                <AnswerForm questions={questions} onSubmit={handleSubmitAnswers} submitting={submitting} />
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="space-y-4">
              {submitError && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
                  {submitError}
                </motion.div>
              )}

              {mappingStatus?.phase === 'processing' && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-center gap-3">
                  <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                  <div>
                    <p className="text-sm text-blue-400 font-medium">Mapping answers to controls…</p>
                    <p className="text-xs text-blue-300/70 mt-0.5">
                      {mappingStatus.scored} of {mappingStatus.total} answers scored so far
                    </p>
                  </div>
                </div>
              )}

              {mappingStatus?.phase === 'done' && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <p className="text-sm text-green-400 font-medium">Questionnaire submitted successfully</p>
                  <p className="text-xs text-green-600 mt-0.5">Review the suggested control mappings below</p>
                </div>
              )}

              {results?.map((result, index) => (
                <motion.div key={result.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: index * 0.05 }} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-5">
                  <div className="flex justify-between items-start mb-3">
                    <p className="text-sm text-white font-medium">Answer {index + 1}</p>
                    <span className="text-xs text-gray-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                      Coverage: {result.coverage_score}%
                    </span>
                  </div>

                  {result.control_mappings?.length > 0 ? (
                    <div className="space-y-2">
                      {result.control_mappings.map(mapping => (
                        <ControlMappingCard key={mapping.control_id} mapping={mapping} onReview={handleReviewMapping} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No controls were mapped to this answer.</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}